/* Data loads — where files come in, and every file anyone has loaded,
 * newest first, for an admin to accept, approve or roll back; and, at the
 * foot, the users.
 *
 * The top panel takes a file of any kind the server lists. An editor's
 * file is staged: kept on disk, shown as awaiting acceptance, and loaded
 * only when an admin accepts it — so the editor sees the same list with
 * no buttons. A viewer is told the page is not theirs rather than shown
 * the server's 403. Deleting a load removes the rows it wrote — for a
 * tracker workbook that leaves its days empty — so the confirm says so by
 * name; rejecting a staged file discards it, and the confirm says that. */

import { useRef, useState, type CSSProperties, type FormEvent } from 'react'
import { MAIN_STYLE, Subline } from '../App'
import { Blueprint, Empty, ErrorBox, Loading, PanelHead } from '../components/ui'
import { useAsync } from '../lib/useAsync'
import { num } from '../lib/format'
import {
  ROLES, ROLE_LABEL, loads, useAuth, useCan, users,
  type Account, type Load, type LoadKind, type Role,
} from '../lib/auth'

const R: CSSProperties = { textAlign: 'right' }

const when = (iso: string | null) => (iso
  ? new Date(iso).toLocaleString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
  : '–')
const day = (d: string | null) => (d
  ? new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
  : null)
const span = (l: Load) => {
  const a = day(l.first_date), b = day(l.last_date)
  return !a ? '–' : !b || a === b ? a : `${a} – ${b}`
}
const isTracker = (kind: string) => /tracker|production/i.test(kind)

export default function Loads() {
  const { user, setPending } = useAuth()
  const load = useCan('load')
  const review = useCan('review')
  const manage = useCan('manage')
  const { data, error, loading, reload, set } = useAsync(() => loads.list(), [])
  const [open, setOpen] = useState<Record<number, boolean>>({})
  const [busy, setBusy] = useState<number | null>(null)
  const [notice, setNotice] = useState<{ text: string; bad?: boolean } | null>(null)

  if (user.role === 'viewer') {
    return (
      <main style={MAIN_STYLE}>
        <Subline>What has been loaded, and by whom</Subline>
        <Empty title="For editors and admins">
          You are signed in as a viewer. The files editors load and the admin's review of them
          are listed here; ask an admin if you need to see or load data.
        </Empty>
      </main>
    )
  }

  const refresh = async () => {
    try {
      const next = await loads.list()
      set(next)
      setPending(next.pending)
    } catch { reload() }
  }

  const approve = async (l: Load) => {
    setBusy(l.id)
    setNotice(null)
    try {
      const r = await loads.approve(l.id)
      setNotice({
        text: l.staged
          ? `${l.filename} accepted and loaded — ${r.rows == null ? 'no rows counted' : `${num(r.rows)} rows`}`
            + (r.days ? `, ${num(r.days)} day${r.days === 1 ? '' : 's'}` : '') + '.'
          : `${l.filename} approved.`,
      })
      await refresh()
    } catch (e: any) {
      // A file that could not be read: the server kept the reason on the
      // row's warnings, so the refreshed list shows it under the name too.
      setNotice({ text: e?.message ?? String(e), bad: true })
      await refresh()
    } finally { setBusy(null) }
  }

  const remove = async (l: Load) => {
    const tracker = isTracker(l.kind)
    const ok = window.confirm(l.staged
      ? `Reject ${l.filename}?\n\nThe file is discarded; nothing was loaded from it.`
      : `Delete ${l.filename}?\n\nThe rows this load wrote will be removed`
        + (tracker ? ', and the days it covered will be left empty' : '') + '. This cannot be undone from here.')
    if (!ok) return
    setBusy(l.id)
    setNotice(null)
    try {
      const r = await loads.remove(l.id)
      const parts = Object.entries(r.removed ?? {}).map(([t, n]) => `${t} ${num(n)}`)
      setNotice({
        text: l.staged
          ? `${l.filename} rejected — the file was discarded.`
          : `${l.filename} deleted — removed ${parts.length ? parts.join(', ') : 'no rows'}`
            + (r.note ? `. ${r.note}` : '.'),
      })
      await refresh()
    } catch (e: any) {
      setNotice({ text: e?.message ?? String(e), bad: true })
    } finally { setBusy(null) }
  }

  const list = data?.loads ?? []
  const pending = data?.pending ?? 0

  return (
    <>
      <Subline>
        {data
          ? `${num(list.length)} load${list.length === 1 ? '' : 's'} on file · ${pending ? `${num(pending)} pending` : 'nothing pending'}`
          : 'What has been loaded, and by whom'}
      </Subline>
      <main style={MAIN_STYLE}>
        {load ? <UploadPanel direct={review} onDone={refresh} /> : null}

        {error ? <ErrorBox error={error} onRetry={reload} /> : null}
        {loading && !data ? <Loading what="the load log" /> : null}

        {data ? (
          <Blueprint className="sheet" style={{ padding: '22px 24px' }}>
            <PanelHead title="Data loads"
              hint={review ? 'Accept what looks right; reject or delete what does not' : 'An admin accepts these'} />
            {notice ? (
              <div style={{ fontSize: 13, marginTop: 10, color: notice.bad ? 'var(--color-bad)' : 'var(--color-good)' }}>
                {notice.text}
              </div>
            ) : null}
            {list.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--color-neutral-600)', padding: '18px 0 6px' }}>
                Nothing has been loaded through the app yet.
              </div>
            ) : (
              <div className="scroll-x" style={{ marginTop: 14 }}>
                <table className="table" style={{ minWidth: 980 }}>
                  <thead>
                    <tr>
                      <th>File</th><th>Kind</th><th>Who</th><th>When</th>
                      <th style={R}>Days</th><th style={R}>Rows</th><th>Dates</th>
                      <th>Status</th><th />
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((l) => {
                      const shown = Boolean(open[l.id])
                      const n = l.warnings?.length ?? 0
                      return (
                        <LoadRows key={l.id} l={l} warningsOpen={shown} warningCount={n}
                          toggle={() => setOpen((o) => ({ ...o, [l.id]: !o[l.id] }))}
                          review={review} busy={busy === l.id}
                          onApprove={() => approve(l)} onDelete={() => remove(l)} />
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Blueprint>
        ) : null}

        {manage ? <UsersPanel /> : null}
      </main>
    </>
  )
}

function LoadRows({ l, warningsOpen, warningCount, toggle, review, busy, onApprove, onDelete }: {
  l: Load; warningsOpen: boolean; warningCount: number; toggle: () => void
  review: boolean; busy: boolean; onApprove: () => void; onDelete: () => void
}) {
  return (
    <>
      <tr>
        <td style={{ fontWeight: 600, maxWidth: 280 }}>
          <div className="truncate" title={l.filename}>{l.filename}</div>
          {warningCount ? (
            <button type="button" onClick={toggle} aria-expanded={warningsOpen} style={{
              font: 'inherit', fontSize: 12, background: 'none', border: 0, padding: 0,
              cursor: 'pointer', color: 'var(--color-warn)',
            }}>
              {warningsOpen ? '▾' : '▸'} {warningCount} warning{warningCount === 1 ? '' : 's'}
            </button>
          ) : null}
        </td>
        <td style={{ whiteSpace: 'nowrap' }}>{l.kind}</td>
        <td style={{ whiteSpace: 'nowrap' }}>{l.uploaded_by ?? '–'}</td>
        <td className="num" style={{ whiteSpace: 'nowrap' }}>{when(l.uploaded_at)}</td>
        <td className="num" style={R}>{l.staged || l.days == null ? '–' : num(l.days)}</td>
        <td className="num" style={R}>
          {l.staged || l.rows == null ? '–' : num(l.rows)}
          {l.rows_replaced ? (
            <div className="muted num" style={{ fontSize: 11 }}>{num(l.rows_replaced)} replaced</div>
          ) : null}
        </td>
        <td className="num" style={{ whiteSpace: 'nowrap' }}>{span(l)}</td>
        <td style={{ whiteSpace: 'nowrap' }}>
          {l.staged ? (
            <span className="tag tag-accent-2">Awaiting acceptance</span>
          ) : l.status === 'pending' ? (
            <span className="tag tag-accent-2">Pending</span>
          ) : (
            <>
              <span className="tag tag-neutral">Approved</span>
              <div className="muted" style={{ fontSize: 11, marginTop: 3 }}>
                {l.reviewed_by ?? '–'} · {when(l.reviewed_at)}
              </div>
            </>
          )}
        </td>
        <td style={{ ...R, whiteSpace: 'nowrap' }}>
          {review ? (
            <>
              {l.status === 'pending' ? (
                <button className="btn btn-primary" type="button" disabled={busy}
                  style={{ fontSize: 12, padding: '5px 12px', marginRight: 6 }} onClick={onApprove}>
                  {l.staged ? (busy ? 'Loading…' : 'Accept') : 'Approve'}
                </button>
              ) : null}
              <button className="btn btn-ghost" type="button" disabled={busy}
                style={{ fontSize: 12, padding: '5px 10px' }} onClick={onDelete}>
                {busy ? 'Working…' : l.staged ? 'Reject' : 'Delete'}
              </button>
            </>
          ) : null}
        </td>
      </tr>
      {warningsOpen && warningCount ? (
        <tr>
          <td colSpan={9} style={{ background: 'color-mix(in srgb, var(--color-warn) 6%, transparent)' }}>
            <ul style={{ margin: '2px 0', paddingLeft: 18, fontSize: 12, color: 'var(--color-neutral-700)' }}>
              {l.warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </td>
        </tr>
      ) : null}
    </>
  )
}

// ---------------------------------------------------------------- upload
/** The one place a file is handed in. The kinds come from the server, so
 *  a new loader shows up here without a page change. `direct` is the
 *  admin's case: the file is loaded as it lands rather than staged. */
function UploadPanel({ direct, onDone }: { direct: boolean; onDone: () => Promise<void> }) {
  const { data: kinds, error, loading } = useAsync(() => loads.kinds(), [])
  const [kindId, setKindId] = useState<string>('')
  const [month, setMonth] = useState('')
  const [drag, setDrag] = useState(false)
  const [busy, setBusy] = useState(false)
  const [status, setStatus] = useState<{ text: string; bad?: boolean } | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  const kind: LoadKind | undefined = kinds?.find((k) => k.id === kindId) ?? kinds?.[0]
  const register = kind?.id === 'attendance'

  const upload = async (files: FileList | null) => {
    if (!kind || !files?.length) return
    const list = kind.multiple ? [...files] : [files[0]]
    setBusy(true)
    setStatus({ text: `${direct ? 'Loading' : 'Sending'} ${list.length} file${list.length === 1 ? '' : 's'}…` })
    try {
      const r = await loads.upload(kind.id, list, register && month.trim() ? month.trim() : undefined)
      const parts: string[] = []
      if (r.loaded.length) {
        parts.push(direct
          ? `${r.loaded.length} file${r.loaded.length === 1 ? '' : 's'} loaded — on the dashboards now.`
          : `${r.loaded.length} file${r.loaded.length === 1 ? '' : 's'} handed in — waiting for an admin to accept.`)
      }
      parts.push(...r.failed.map((f) => `${f.filename}: ${f.error}`))
      setStatus({ text: parts.join(' '), bad: r.failed.length > 0 && r.loaded.length === 0 })
      await onDone()
    } catch (e: any) {
      setStatus({ text: e?.message ?? String(e), bad: true })
    } finally {
      setBusy(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  const control: CSSProperties = { fontSize: 13, padding: '6px 8px' }
  const eyebrow: CSSProperties = {
    fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--color-neutral-600)',
  }

  return (
    <Blueprint className="sheet" style={{ padding: '22px 24px' }}>
      <PanelHead title="Load a file"
        hint={direct
          ? 'Loaded at once — it is on the dashboards as soon as it is read'
          : 'Held until an admin accepts it; only then does it reach the dashboards'} />
      {error ? <div style={{ marginTop: 12 }}><ErrorBox error={error} /></div> : null}
      {loading && !kinds ? <Loading what="the kinds of file" /> : null}
      {kinds && kind ? (
        <div style={{
          display: 'grid', gridTemplateColumns: 'minmax(260px, 360px) minmax(280px, 1fr)',
          gap: '16px 28px', marginTop: 16, alignItems: 'start',
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <span style={eyebrow}>What the file is</span>
              <select className="input" value={kind.id} disabled={busy} style={control}
                onChange={(e) => { setKindId(e.target.value); setStatus(null) }}>
                {kinds.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
              </select>
              <span className="pretty" style={{ fontSize: 12, color: 'var(--color-neutral-700)', lineHeight: 1.5 }}>
                {kind.blurb}
              </span>
            </label>
            {register ? (
              <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <span style={eyebrow}>Register month</span>
                <input className="input" type="month" value={month} disabled={busy}
                  style={{ ...control, width: 180 }} onChange={(e) => setMonth(e.target.value)} />
                <span style={{ fontSize: 12, color: 'var(--color-neutral-600)' }}>
                  Only needed when the register's file name does not carry its month.
                </span>
              </label>
            ) : null}
          </div>
          <label
            onDrop={(e) => { e.preventDefault(); setDrag(false); if (!busy) upload(e.dataTransfer.files) }}
            onDragOver={(e) => { e.preventDefault(); if (!drag) setDrag(true) }}
            onDragLeave={() => setDrag(false)}
            style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              gap: 6, minHeight: 120, padding: '18px 20px', textAlign: 'center',
              cursor: busy ? 'progress' : 'pointer',
              border: `1px dashed ${drag ? 'var(--color-accent)' : 'var(--color-divider)'}`,
              background: drag ? 'var(--color-accent-100)' : 'transparent',
            }}>
            <div style={{ fontFamily: 'var(--font-heading)', fontSize: 18 }}>
              {busy ? 'Reading…' : `Drop ${kind.multiple ? 'files' : 'a file'} here, or click to choose`}
            </div>
            <div style={{ fontSize: 12, color: 'var(--color-neutral-600)' }}>
              {kind.accept.join(', ')}{kind.multiple ? ' · several at once' : ' · one at a time'}
            </div>
            <input ref={fileRef} type="file" accept={kind.accept.join(',')} multiple={kind.multiple}
              disabled={busy} onChange={(e) => upload(e.target.files)} style={{ display: 'none' }} />
          </label>
        </div>
      ) : null}
      {status ? (
        <div style={{ fontSize: 13, marginTop: 12, color: status.bad ? 'var(--color-bad)' : 'var(--color-accent-700)' }}>
          {status.text}
        </div>
      ) : null}
    </Blueprint>
  )
}

// ----------------------------------------------------------------- users
/** The accounts: who may sign in and as what. Passwords are set here and
 *  never shown again — the page does not hold one past the request. */
function UsersPanel() {
  const { user: me } = useAuth()
  const { data, error, loading, reload, set } = useAsync(() => users.list(), [])
  const [notice, setNotice] = useState<{ text: string; bad?: boolean } | null>(null)
  const [busy, setBusy] = useState(false)
  const [name, setName] = useState('')
  const [role, setRole] = useState<Role>('viewer')
  const [password, setPassword] = useState('')

  const say = (text: string, bad = false) => setNotice({ text, bad })
  const fail = (e: any) => say(e?.message ?? String(e), true)

  const add = async (e: FormEvent) => {
    e.preventDefault()
    const username = name.trim()
    if (!username || !password) { say('A username and a password are both needed.', true); return }
    setBusy(true)
    try {
      await users.create({ username, password, role })
      setName(''); setPassword(''); setRole('viewer')
      say(`${username} added as ${ROLE_LABEL[role].toLowerCase()}.`)
      set(await users.list())
    } catch (err) { fail(err) } finally { setBusy(false) }
  }

  const patch = async (u: Account, body: { role?: Role; active?: boolean; password?: string }, done: string) => {
    setBusy(true)
    try {
      await users.update(u.id, body)
      say(done)
      set(await users.list())
    } catch (err) { fail(err) } finally { setBusy(false) }
  }

  const resetPassword = (u: Account) => {
    const pw = window.prompt(`New password for ${u.username}:`)
    if (pw === null) return
    if (!pw) { say('The password cannot be empty.', true); return }
    patch(u, { password: pw }, `Password reset for ${u.username}.`)
  }

  const list = data ?? []
  const control: CSSProperties = { fontSize: 13, padding: '5px 8px' }

  return (
    <Blueprint className="sheet" style={{ padding: '22px 24px' }}>
      <PanelHead title="Users" hint={data ? `${num(list.length)} account${list.length === 1 ? '' : 's'}` : undefined} />
      {error ? <div style={{ marginTop: 12 }}><ErrorBox error={error} onRetry={reload} /></div> : null}
      {loading && !data ? <Loading what="the users" /> : null}
      {notice ? (
        <div style={{ fontSize: 13, marginTop: 10, color: notice.bad ? 'var(--color-bad)' : 'var(--color-good)' }}>
          {notice.text}
        </div>
      ) : null}

      {data ? (
        <div className="scroll-x" style={{ marginTop: 14 }}>
          <table className="table" style={{ minWidth: 640 }}>
            <thead>
              <tr><th>Username</th><th>Role</th><th>Active</th><th>Since</th><th /></tr>
            </thead>
            <tbody>
              {list.map((u) => {
                const self = u.username === me.username
                return (
                  <tr key={u.id}>
                    <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {u.username}{self ? <span className="muted" style={{ fontWeight: 400 }}> (you)</span> : null}
                    </td>
                    <td>
                      <select value={u.role} disabled={busy || self} style={control}
                        onChange={(e) => patch(u, { role: e.target.value as Role },
                          `${u.username} is now ${ROLE_LABEL[e.target.value as Role].toLowerCase()}.`)}>
                        {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
                      </select>
                    </td>
                    <td>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                        <input type="checkbox" checked={u.active} disabled={busy || self}
                          onChange={(e) => patch(u, { active: e.target.checked },
                            `${u.username} ${e.target.checked ? 'can sign in again' : 'can no longer sign in'}.`)} />
                        {u.active ? 'Active' : 'Disabled'}
                      </label>
                    </td>
                    <td className="num muted" style={{ whiteSpace: 'nowrap', fontSize: 12 }}>{when(u.created_at)}</td>
                    <td style={R}>
                      <button className="btn btn-ghost" type="button" disabled={busy}
                        style={{ fontSize: 12, padding: '5px 10px' }} onClick={() => resetPassword(u)}>
                        Reset password
                      </button>
                    </td>
                  </tr>
                )
              })}
              <tr>
                <td colSpan={5} style={{ paddingTop: 12 }}>
                  <form onSubmit={add} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span className="axis-title">Add</span>
                    <input className="input" type="text" placeholder="username" autoComplete="off"
                      autoCapitalize="none" spellCheck={false} value={name} disabled={busy}
                      style={{ ...control, width: 160 }} onChange={(e) => setName(e.target.value)} />
                    <select value={role} disabled={busy} style={control}
                      onChange={(e) => setRole(e.target.value as Role)}>
                      {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
                    </select>
                    <input className="input" type="password" placeholder="password" autoComplete="new-password"
                      value={password} disabled={busy}
                      style={{ ...control, width: 160 }} onChange={(e) => setPassword(e.target.value)} />
                    <button className="btn btn-secondary" type="submit" disabled={busy}
                      style={{ fontSize: 12, padding: '5px 12px' }}>
                      {busy ? 'Working…' : 'Add user'}
                    </button>
                  </form>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      ) : null}
      <div className="pretty" style={{ fontSize: 12, color: 'var(--color-neutral-600)', marginTop: 12, maxWidth: '90ch' }}>
        A viewer reads every page; an editor also loads data files; an admin also reviews the
        loads above and keeps this list. Disabling an account keeps its name on the loads it made.
        Your own role and active flag are changed by another admin, not from here.
      </div>
    </Blueprint>
  )
}
